/**
 * Wealth Gap Print Content Component
 * Custom structured layout for PDF generation with proper page breaks
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Target, DollarSign, Calendar, TrendingUp } from 'lucide-react';
import { formatCurrency } from '../../utils/enhancedDashboardUtils';

const WealthGapPrintContent = ({ wealthGap }) => {
  if (!wealthGap) return null;

  // Check calculation method
  const isSingleNumber = wealthGap.wealth_goal_method === 'single_number';
  const isMonthlyNeeds = wealthGap.wealth_goal_method === 'monthly_needs';

  // Get required sale price data
  const requiredData = wealthGap.required_sale_price_data || {};
  const hasRequiredData = isMonthlyNeeds && requiredData.total_capital_needed !== undefined;

  // Calculate key metrics
  const currentYear = new Date().getFullYear();
  const yearsUntilExit = Math.max(0, (wealthGap.exit_year || currentYear + 2) - currentYear);
  const ageAtExit = wealthGap.current_age + yearsUntilExit;
  const yearsUntilRetirement = Math.max(0, wealthGap.retirement_age - wealthGap.current_age);
  const yearsToLifeExpectancy = (wealthGap.life_expectancy || 100) - wealthGap.current_age;

  // Calculate outside income growth
  const outsideIncomeGrowthRate = (wealthGap.outside_income_annual_increase || 0) / 100;
  const futureOutsideIncomeMonthly = (wealthGap.outside_income_monthly || 0) * Math.pow(1 + outsideIncomeGrowthRate, yearsUntilRetirement);

  // Calculate monthly need at exit with inflation
  // Sum both lifestyle need and non-business cash needs
  const totalCurrentMonthlyNeed = (wealthGap.monthly_cash_need || 0) + (wealthGap.non_business_monthly_cash_needs || 0);
  const inflationMultiplier = Math.pow(1 + (wealthGap.annual_inflation_rate || 2) / 100, yearsUntilExit);
  const monthlyNeedAtExit = totalCurrentMonthlyNeed * inflationMultiplier * (1 + (wealthGap.lifestyle_change_percentage || 0) / 100);

  // Current and future net monthly needs
  const currentNetMonthlyNeed = Math.max(0, totalCurrentMonthlyNeed - (wealthGap.outside_income_monthly || 0));
  const netMonthlyNeed = Math.max(0, monthlyNeedAtExit - futureOutsideIncomeMonthly);

  // Ownership and tax rates
  const ownershipDecimal = (wealthGap.ownership_percentage || 100) / 100;
  const taxDecimal = (wealthGap.tax_rate_on_sale || 42) / 100;

  // After-tax proceeds from future business value (if provided)
  const futureBusinessValueDisplay = wealthGap.future_business_value || wealthGap.desired_sale_price || 0;
  const desiredAfterTaxProceeds = futureBusinessValueDisplay
    ? futureBusinessValueDisplay * ownershipDecimal * (1 - taxDecimal)
    : 0;

  // Helper function to calculate FV with monthly withdrawals (growing annuity)
  const calculateFVwithPMT = (pv, annualRate, years, monthlyPmt, annualGrowthRate = 0) => {
    if (years === 0) return pv;
    const monthlyRate = annualRate / 12 / 100;
    const monthlyGrowthRate = annualGrowthRate / 12 / 100;
    const n = years * 12; // Total months

    // Calculate FV of the present value
    const fvPV = pv * Math.pow(1 + monthlyRate, n);

    // Calculate FV of growing withdrawals
    if (monthlyPmt === 0) {
      return fvPV;
    }

    // Handle edge case where return rate equals growth rate
    if (Math.abs(monthlyRate - monthlyGrowthRate) < 1e-10) {
      // When r = g: FV = PV(1+r)^n - PMT * n * (1+r)^(n-1)
      const fvPMT = monthlyPmt * n * Math.pow(1 + monthlyRate, n - 1);
      return fvPV - fvPMT;
    }

    // Normal case: Growing annuity formula
    const fvPMT = monthlyPmt * ((Math.pow(1 + monthlyRate, n) - Math.pow(1 + monthlyGrowthRate, n)) / (monthlyRate - monthlyGrowthRate));
    return fvPV - fvPMT;
  };

  // Calculate monthly withdrawal from the selected source
  const monthlyWithdrawal = wealthGap.monthly_cash_need_from_non_business || 0;
  const nonBusinessSourceCategory = wealthGap.non_business_source_category || 'liquid_assets';
  const annualInflationRate = wealthGap.annual_inflation_rate || 2;

  // Business sale proceeds calculations
  const futureBusinessValue = wealthGap.future_business_value || wealthGap.desired_sale_price || 0;
  const grossProceeds = futureBusinessValue * ownershipDecimal;

  // Lump sum calculations
  const lumpSumPercent = wealthGap.lump_sum_percent || 0;
  const lumpSumGross = grossProceeds * (lumpSumPercent / 100);
  const lumpSumAfterTax = lumpSumGross * (1 - taxDecimal);

  // Lump sum allocation percentages (default to 100% Other Investments if not set)
  const lumpSumAllocLiquid = wealthGap.lump_sum_allocation_liquid_assets || 0;
  const lumpSumAllocRealEstate = wealthGap.lump_sum_allocation_real_estate || 0;
  const lumpSumAllocOtherInv = wealthGap.lump_sum_allocation_other_investments !== undefined
    ? wealthGap.lump_sum_allocation_other_investments
    : 100;

  // Lump sum amounts by asset category
  const lumpSumToLiquid = lumpSumAfterTax * (lumpSumAllocLiquid / 100);
  const lumpSumToRealEstate = lumpSumAfterTax * (lumpSumAllocRealEstate / 100);
  const lumpSumToOtherInv = lumpSumAfterTax * (lumpSumAllocOtherInv / 100);

  // Earnout calculations
  const earnoutGross = grossProceeds * (1 - lumpSumPercent / 100);
  const earnoutYears = wealthGap.earnout_years || 5;
  const earnoutTaxRate = wealthGap.earnout_tax_rate || 42;
  const earnoutTaxDecimal = earnoutTaxRate / 100;
  const earnoutPaymentFrequency = wealthGap.earnout_payment_frequency || 'annual';
  const paymentsPerYear = earnoutPaymentFrequency === 'monthly' ? 12 : 1;
  const totalEarnoutPayments = earnoutYears * paymentsPerYear;
  const earnoutPaymentGross = totalEarnoutPayments > 0 ? earnoutGross / totalEarnoutPayments : 0;
  const earnoutPaymentAfterTax = earnoutPaymentGross * (1 - earnoutTaxDecimal);

  // Earnout allocation percentages (default to 100% Other Investments if not set)
  const earnoutAllocLiquid = wealthGap.earnout_allocation_liquid_assets || 0;
  const earnoutAllocRealEstate = wealthGap.earnout_allocation_real_estate || 0;
  const earnoutAllocOtherInv = wealthGap.earnout_allocation_other_investments !== undefined
    ? wealthGap.earnout_allocation_other_investments
    : 100;

  // Earnout payment amounts by asset category (per payment)
  const earnoutPaymentToLiquid = earnoutPaymentAfterTax * (earnoutAllocLiquid / 100);
  const earnoutPaymentToRealEstate = earnoutPaymentAfterTax * (earnoutAllocRealEstate / 100);
  const earnoutPaymentToOtherInv = earnoutPaymentAfterTax * (earnoutAllocOtherInv / 100);

  // Calculate FV for each asset class (with growing annuity if withdrawal source)
  const liquidAssetsRate = wealthGap.liquid_assets_return_rate !== undefined ? wealthGap.liquid_assets_return_rate : 3;
  const retirementAccountsRate = wealthGap.retirement_accounts_return_rate !== undefined ? wealthGap.retirement_accounts_return_rate : 7;
  const realEstateRate = wealthGap.real_estate_return_rate !== undefined ? wealthGap.real_estate_return_rate : 4;
  const otherInvestmentsRate = wealthGap.other_investments_return_rate !== undefined ? wealthGap.other_investments_return_rate : 6;

  // Calculate base FV (current assets with withdrawals)
  const liquidAssetsFVBase = nonBusinessSourceCategory === 'liquid_assets'
    ? calculateFVwithPMT(wealthGap.liquid_assets || 0, liquidAssetsRate, yearsUntilExit, monthlyWithdrawal, annualInflationRate)
    : (wealthGap.liquid_assets || 0) * Math.pow(1 + liquidAssetsRate / 100, yearsUntilExit);

  const retirementAccountsFVBase = nonBusinessSourceCategory === 'retirement_accounts'
    ? calculateFVwithPMT(wealthGap.retirement_accounts || 0, retirementAccountsRate, yearsUntilExit, monthlyWithdrawal, annualInflationRate)
    : (wealthGap.retirement_accounts || 0) * Math.pow(1 + retirementAccountsRate / 100, yearsUntilExit);

  const realEstateFVBase = nonBusinessSourceCategory === 'real_estate'
    ? calculateFVwithPMT(wealthGap.real_estate_equity || 0, realEstateRate, yearsUntilExit, monthlyWithdrawal, annualInflationRate)
    : (wealthGap.real_estate_equity || 0) * Math.pow(1 + realEstateRate / 100, yearsUntilExit);

  const otherInvestmentsFVBase = nonBusinessSourceCategory === 'other_investments'
    ? calculateFVwithPMT(wealthGap.other_investments || 0, otherInvestmentsRate, yearsUntilExit, monthlyWithdrawal, annualInflationRate)
    : (wealthGap.other_investments || 0) * Math.pow(1 + otherInvestmentsRate / 100, yearsUntilExit);

  // Add lump sum proceeds (received at exit, so grows for remaining years to life expectancy)
  const yearsFromExitToLifeExpectancy = yearsToLifeExpectancy - yearsUntilExit;
  const lumpSumLiquidFV = lumpSumToLiquid * Math.pow(1 + liquidAssetsRate / 100, yearsFromExitToLifeExpectancy);
  const lumpSumRealEstateFV = lumpSumToRealEstate * Math.pow(1 + realEstateRate / 100, yearsFromExitToLifeExpectancy);
  const lumpSumOtherInvFV = lumpSumToOtherInv * Math.pow(1 + otherInvestmentsRate / 100, yearsFromExitToLifeExpectancy);

  // Add earnout proceeds (annuity payments starting at exit, continuing for earnout years)
  // For monthly payments, convert to monthly rate; for annual, use annual
  const earnoutPaymentsPerYear = earnoutPaymentFrequency === 'monthly' ? 12 : 1;
  const earnoutPeriodInYears = Math.min(earnoutYears, yearsFromExitToLifeExpectancy); // Can't exceed remaining life

  // Calculate FV of earnout annuity for each asset class
  // Each payment grows from when it's received until life expectancy
  const earnoutLiquidFV = earnoutPaymentFrequency === 'monthly'
    ? calculateFVwithPMT(0, liquidAssetsRate, earnoutPeriodInYears, earnoutPaymentToLiquid, 0) * Math.pow(1 + liquidAssetsRate / 100, yearsFromExitToLifeExpectancy - earnoutPeriodInYears)
    : calculateFVwithPMT(0, liquidAssetsRate, earnoutPeriodInYears, earnoutPaymentToLiquid * 12, 0) * Math.pow(1 + liquidAssetsRate / 100, yearsFromExitToLifeExpectancy - earnoutPeriodInYears);

  const earnoutRealEstateFV = earnoutPaymentFrequency === 'monthly'
    ? calculateFVwithPMT(0, realEstateRate, earnoutPeriodInYears, earnoutPaymentToRealEstate, 0) * Math.pow(1 + realEstateRate / 100, yearsFromExitToLifeExpectancy - earnoutPeriodInYears)
    : calculateFVwithPMT(0, realEstateRate, earnoutPeriodInYears, earnoutPaymentToRealEstate * 12, 0) * Math.pow(1 + realEstateRate / 100, yearsFromExitToLifeExpectancy - earnoutPeriodInYears);

  const earnoutOtherInvFV = earnoutPaymentFrequency === 'monthly'
    ? calculateFVwithPMT(0, otherInvestmentsRate, earnoutPeriodInYears, earnoutPaymentToOtherInv, 0) * Math.pow(1 + otherInvestmentsRate / 100, yearsFromExitToLifeExpectancy - earnoutPeriodInYears)
    : calculateFVwithPMT(0, otherInvestmentsRate, earnoutPeriodInYears, earnoutPaymentToOtherInv * 12, 0) * Math.pow(1 + otherInvestmentsRate / 100, yearsFromExitToLifeExpectancy - earnoutPeriodInYears);

  // Future value at exit (personal assets only, no business proceeds)
  const liquidAssetsFV = liquidAssetsFVBase;
  const retirementAccountsFV = retirementAccountsFVBase;
  const realEstateFV = realEstateFVBase;
  const otherInvestmentsFV = otherInvestmentsFVBase;

  // Calculate future liabilities
  const futureLiabilities = (wealthGap.total_liabilities || 0) * Math.pow(1 - (wealthGap.liability_reduction_rate || 0) / 100, yearsUntilExit);

  // Total future net worth at exit (personal assets only, no business proceeds)
  const futureNetWorth = liquidAssetsFV + retirementAccountsFV + realEstateFV + otherInvestmentsFV - futureLiabilities;

  // Total FV at life expectancy for each asset class (base + lump sum + earnout)
  const liquidAssetsFVAtLifeExpectancy = liquidAssetsFVBase + lumpSumLiquidFV + earnoutLiquidFV;
  const retirementAccountsFVAtLifeExpectancy = retirementAccountsFVBase;
  const realEstateFVAtLifeExpectancy = realEstateFVBase + lumpSumRealEstateFV + earnoutRealEstateFV;
  const otherInvestmentsFVAtLifeExpectancy = otherInvestmentsFVBase + lumpSumOtherInvFV + earnoutOtherInvFV;

  // Calculate wealth gap/surplus using locally calculated futureNetWorth
  const totalCapitalNeeded = requiredData.total_capital_needed || 0;
  const wealthGapAmount = totalCapitalNeeded - futureNetWorth;
  const localWealthSurplus = Math.max(0, -wealthGapAmount); // Positive if surplus
  const localNetGapFromBusiness = Math.max(0, wealthGapAmount); // Positive if gap

  // Calculate required sale price from local net gap
  // Required Sale Price = After-Tax Proceeds Needed / (Ownership% * (1 - Tax%))
  const localRequiredSalePrice = localNetGapFromBusiness > 0
    ? localNetGapFromBusiness / (ownershipDecimal * (1 - taxDecimal))
    : 0;

  // Calculate after-tax proceeds from locally calculated required sale price
  const afterTaxProceeds = localRequiredSalePrice * ownershipDecimal * (1 - taxDecimal);

  return (
    <>
      {/* PAGE 2: Wealth Gap Analysis + Calculation Method */}
      <div className="print-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>

        <div style={{ padding: '0.15in 0' }}>

          {/* Wealth Gap Analysis Summary */}
          {isSingleNumber ? (
            /* Single Number Method Display */
            <div>
              <h4 className="font-bold text-xl text-gray-900 mb-3 flex items-center gap-2">
                Wealth Gap Analysis
              </h4>
              <div className="space-y-3">
                {/* Wealth Goal */}
                <div className="bg-white rounded-lg p-4 border-2 border-blue-300">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-700 font-semibold text-base">Wealth Goal:</span>
                    <span className="text-3xl font-bold text-blue-600">
                      {formatCurrency(wealthGap.wealth_goal_amount || 0)}
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 mt-2">
                    Your target wealth goal at exit
                  </p>
                </div>

                {/* Current Personal Net Worth */}
                <div className="bg-white rounded-lg p-4 border-2 border-emerald-300">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-700 font-semibold text-base">Current Personal Net Worth:</span>
                    <span className="text-3xl font-bold text-emerald-600">
                      {formatCurrency(wealthGap.non_business_net_worth || 0)}
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 mt-2">
                    Your current net worth excluding business value
                  </p>
                </div>

                {/* Gap/Surplus Calculation */}
                {(() => {
                  const wealthGoal = wealthGap.wealth_goal_amount || 0;
                  const personalNetWorth = wealthGap.non_business_net_worth || 0;
                  const gap = wealthGoal - personalNetWorth;
                  const hasGap = gap > 0;

                  return (
                    <div className={`bg-white rounded-lg p-4 border-2 ${hasGap ? 'border-orange-300' : 'border-green-300'}`}>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-gray-700 font-semibold text-base">{hasGap ? 'Wealth Gap:' : 'Wealth Surplus:'}</span>
                        <span className={`text-3xl font-bold ${hasGap ? 'text-orange-600' : 'text-green-600'}`}>
                          {formatCurrency(Math.abs(gap))}
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 mt-2">
                        {hasGap
                          ? 'Amount needed from business and other sources to reach your wealth goal'
                          : 'You already exceed your wealth goal! Any business sale will add to your surplus.'}
                      </p>
                    </div>
                  );
                })()}

                {/* Timeline */}
                <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
                  <h5 className="font-semibold text-purple-900 mb-2 text-sm">Timeline & Key Details</h5>
                  <div className="grid grid-cols-4 gap-2">
                    <div>
                      <p className="text-xs text-purple-700">Current Age</p>
                      <p className="text-base font-bold text-purple-900">{wealthGap.current_age}</p>
                    </div>
                    <div>
                      <p className="text-xs text-purple-700">Exit Year</p>
                      <p className="text-base font-bold text-purple-900">{wealthGap.exit_year}</p>
                    </div>
                    <div>
                      <p className="text-xs text-purple-700">Exit Age</p>
                      <p className="text-base font-bold text-purple-900">{ageAtExit}</p>
                    </div>
                    <div>
                      <p className="text-xs text-purple-700">Years Until Exit</p>
                      <p className="text-base font-bold text-purple-900">{yearsUntilExit}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* Monthly Needs Method Display */
            <div className={`border-2 rounded-xl p-4 mb-4 ${hasRequiredData && localWealthSurplus > 0 ? 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-200' : 'bg-gradient-to-br from-orange-50 to-red-50 border-orange-200'}`}>
              <h4 className="font-bold text-xl text-gray-900 mb-3 flex items-center gap-2">
                Wealth Gap Analysis
              </h4>

              {hasRequiredData ? (
                <div className="space-y-3">
                  {localWealthSurplus > 0 ? (
                    <div className="bg-white rounded-lg p-4 border-2 border-green-300">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-gray-700 font-semibold text-base">Wealth Surplus:</span>
                        <span className="text-3xl font-bold text-green-600">
                          {formatCurrency(localWealthSurplus)}
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 mt-2">
                        Great news! Your personal net worth at exit may exceed your lifestyle needs. The business sale may add to your wealth, but may not be required to sustain your lifestyle.
                      </p>
                    </div>
                  ) : (
                    <div className="bg-white rounded-lg p-4 border-2 border-orange-300">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-gray-700 font-semibold text-base">Required from Business Sale:</span>
                        <span className="text-3xl font-bold text-orange-600">
                          {formatCurrency(localNetGapFromBusiness)}
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 mt-2">
                        After accounting for your personal net worth at exit, this is the net after-tax proceeds your business may need to provide to support your post-exit lifestyle goals.
                      </p>
                    </div>
                  )}

                  {futureBusinessValueDisplay > 0 && (
                    <div className="bg-white rounded-lg p-3 border border-blue-200">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-700 font-medium text-sm">Future Business Value at Exit:</span>
                        <span className="text-xl font-bold text-blue-600">
                          {formatCurrency(futureBusinessValueDisplay)}
                        </span>
                      </div>
                      <div className="mt-2 pt-2 border-t border-gray-200">
                        {localWealthSurplus > 0 ? (
                          <p className="text-xxs text-green-600 font-semibold">
                            ✓ Your personal net worth may cover your needs. This future business value may be additional wealth!
                          </p>
                        ) : futureBusinessValueDisplay >= localRequiredSalePrice ? (
                          <p className="text-xs text-green-600 font-semibold">
                            ✓ Your future business value exceeds the required amount by {formatCurrency(futureBusinessValueDisplay - localRequiredSalePrice)}
                          </p>
                        ) : (
                          <p className="text-xs text-red-600 font-semibold">
                            ⚠ Your future business value is {formatCurrency(localRequiredSalePrice - futureBusinessValueDisplay)} below the required amount
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <p className="text-sm text-gray-600">Wealth gap data not available</p>
                </div>
              )}
            </div>
          )}

          {/* Calculation Method Description */}
          {isSingleNumber && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
              <h4 className="font-bold text-base text-gray-900 mb-2">Calculation Method</h4>
              <div className="space-y-2 text-xs text-gray-700">
                <p>This wealth gap analysis uses the <span className="font-semibold">"Single Number Method"</span> which compares your target wealth goal to your current personal net worth to determine the gap that needs to be filled from business proceeds and other sources.</p>
                <div className="bg-white rounded p-3 border border-blue-200 space-y-1">
                  <p className="font-semibold text-blue-900">Key Steps:</p>
                  <ol className="list-decimal list-inside space-y-1 ml-2">
                    <li>Determine your target wealth goal at exit</li>
                    <li>Calculate your current personal net worth (excluding business)</li>
                    <li>Calculate the difference to determine wealth gap or surplus</li>
                  </ol>
                </div>
                <p className="text-xs text-gray-600 italic mt-2">
                  Note: This is a simplified calculation. Consider consulting with a financial advisor for a comprehensive analysis that accounts for asset growth, inflation, and tax optimization strategies.
                </p>
              </div>
              {/* Disclaimer & Disclosure (Full Width) */}
              <div className="bg-rose-50 border border-rose-300 rounded-lg p-2 mt-3">
                <h5 className="font-semibold text-rose-900 mb-1 text-sm">Disclosure & Disclaimer</h5>
                <div className="text-xs text-rose-700">
                  <p>This analysis calculates total capital needed and accounts for your personal net worth growth to determine what your business must provide. The calculation may not fit all situations or local market conditions. This is a best-effort, automated projection based on the assumptions above. <span className="font-semibold">Consult with a financial advisor and/or accountant for personalized guidance.</span></p>
                </div>
              </div>
            </div>
          )}

          {!isSingleNumber && (
            <>
              <h4 className="font-bold text-xs text-gray-900 mb-2 flex items-center gap-2">
                Financial Summary at Exit ({wealthGap.exit_year || currentYear + 2})
              </h4>

              {hasRequiredData && (
                <div className="space-y-2">
                  {/* Summary Cards */}
                  <div className="grid grid-cols-3 gap-2">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-1">
                      <p className="text-xs text-blue-700 mb-1">Total Capital Needed</p>
                      <div className="flex justify-between items-baseline">
                        <span className="text-xs text-blue-800">To sustain lifestyle:</span>
                        <span className="font-bold text-sm text-blue-700">{formatCurrency(requiredData.total_capital_needed)}</span>
                      </div>
                      <p className="text-xs text-blue-600 mt-1">Present value of all future spending</p>
                    </div>

                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-1">
                      <p className="text-xs text-emerald-700 mb-1">Personal Net Worth Growth</p>
                      <div className="flex justify-between items-baseline">
                        <span className="text-xs text-emerald-800">Current:</span>
                        <span className="font-semibold text-sm text-emerald-900">{formatCurrency(wealthGap.current_net_worth)}</span>
                      </div>
                      <div className="flex justify-between items-baseline mt-0.5">
                        <span className="text-xs text-emerald-800">At {yearsUntilExit} years:</span>
                        <span className="font-bold text-sm text-emerald-700">{formatCurrency(futureNetWorth)}</span>
                      </div>
                    </div>

                    <div className={`rounded-lg p-2 border-2 ${localWealthSurplus > 0 ? 'bg-green-50 border-green-300' : 'bg-orange-50 border-orange-300'}`}>
                      <p className={`text-xs mb-1 ${localWealthSurplus > 0 ? 'text-green-700' : 'text-orange-700'}`}>
                        {localWealthSurplus > 0 ? 'Wealth Surplus' : 'Gap from Business'}
                      </p>
                      <div className="flex justify-between items-baseline">
                        <span className={`text-xs ${localWealthSurplus > 0 ? 'text-green-800' : 'text-orange-800'}`}>
                          {localWealthSurplus > 0 ? 'Surplus:' : 'Needed from sale:'}
                        </span>
                        <span className={`font-bold text-base ${localWealthSurplus > 0 ? 'text-green-700' : 'text-orange-700'}`}>
                          {formatCurrency(localWealthSurplus > 0 ? localWealthSurplus : localNetGapFromBusiness)}
                        </span>
                      </div>
                    </div>
                  </div>
              </div>
              )}
              
              {/* Key Financial Metrics */}
              {hasRequiredData && (
                <div className="bg-gray-50 border border-gray-300 rounded-lg p-3 mt-4">
                  <div className="space-y-1 text-xs">

                    {/* Last 4 metrics - 2x2 grid */}
                    <div className="grid grid-cols-2 gap-1">
                      <div className="bg-white rounded p-2 border border-gray-200 flex justify-between items-center">
                        <p className="text-gray-700">Exit Year</p>
                        <p className="text-sm font-bold text-purple-900">{wealthGap.exit_year || currentYear + 2}</p>
                      </div>
                      <div className="bg-white rounded p-2 border border-gray-200 flex justify-between items-center">
                        <p className="text-gray-700">Tax Rate</p>
                        <p className="text-sm font-bold text-red-900">{wealthGap.tax_rate_on_sale || 42}%</p>
                      </div>
                      <div className="bg-white rounded p-2 border border-gray-200 flex justify-between items-center">
                        <p className="text-gray-700">Your Share</p>
                        <p className="text-sm font-bold text-indigo-900">{wealthGap.ownership_percentage || 100}%</p>
                      </div>
                      <div className="bg-white rounded p-2 border border-gray-200 flex justify-between items-center">
                        <p className="text-gray-700">After-Tax Proceeds Needed</p>
                        <p className="text-sm font-bold text-gray-900">
                          {formatCurrency(afterTaxProceeds)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Calculation Method for Monthly Needs */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                <h4 className="font-bold text-base text-gray-900 mb-2">Calculation Method</h4>
                <div className="space-y-2 text-xs text-gray-700">
                  <p>This wealth gap analysis uses the <span className="font-semibold">"Funded Lifestyle Method"</span> which calculates how much capital you may need to sustain your desired lifestyle throughout retirement, based on your spending needs, outside income sources, and investment returns.</p>
                  <div className="bg-white rounded p-3 border border-blue-200 space-y-1">
                    <p className="font-semibold text-blue-900">Key Steps:</p>
                    <ol className="list-decimal list-inside space-y-1 ml-2">
                      <li>Calculate your net monthly cash need (after outside income)</li>
                      <li>Project this need to your exit year (adjusted for inflation and lifestyle changes)</li>
                      <li>Determine total capital needed to fund retirement using safe withdrawal rate</li>
                      <li>Project your personal net worth to exit year</li>
                      <li>Calculate the gap between capital needed and future net worth</li>
                      <li>Determine required after-tax proceeds from business sale (accounting for taxes and ownership)</li>
                    </ol>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* PAGE 3: Calculation Assumptions - Only for Monthly Needs Method */}
      {isMonthlyNeeds && (
        <div className="print-page">
          <div className="pdf-header">
            <img src="/dominion-header.png" alt="Dominion Business Advisors" />
          </div>

          <div style={{ padding: '0.1in 0' }}>
            <h4 className="font-bold text-base text-gray-900 mb-2 flex items-center gap-2">
              Calculation Assumptions
            </h4>

            <div className="space-y-3">
              {/* Row 1: Timeline (Full Width) */}
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-2">
                <h5 className="font-semibold text-purple-900 mb-1 text-sm">Timeline</h5>
                <div className="grid grid-cols-5 gap-3">
                  <div>
                    <p className="text-xs text-purple-700">Current Age</p>
                    <p className="text-lg font-bold text-purple-900">{wealthGap.current_age}</p>
                  </div>
                  <div>
                    <p className="text-xs text-purple-700">Retirement Age</p>
                    <p className="text-lg font-bold text-purple-900">{wealthGap.retirement_age}</p>
                  </div>
                  <div>
                    <p className="text-xs text-purple-700">Life Expectancy</p>
                    <p className="text-lg font-bold text-purple-900">{wealthGap.life_expectancy || 95}</p>
                  </div>
                  <div>
                    <p className="text-xs text-purple-700">Exit Year</p>
                    <p className="text-lg font-bold text-purple-900">{wealthGap.exit_year || currentYear + 2}</p>
                    <p className="text-xs text-purple-600">(age {ageAtExit})</p>
                  </div>
                  <div>
                    <p className="text-xs text-purple-700">Years Post-Exit</p>
                    <p className="text-lg font-bold text-purple-900">{(wealthGap.life_expectancy || 95) - ageAtExit}</p>
                  </div>
                </div>
              </div>

              {/* Row 2: Spending Habits and Outside Income Sources */}
              <div className="grid grid-cols-2 gap-3">
                {/* Cash Flow Allocation */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-2">
                  <h5 className="font-semibold text-blue-900 mb-1 text-sm">Cash Flow Allocation</h5>
                  <div className="space-y-1">
                    {/* Total Monthly Need */}
                    <div className="bg-white/70 rounded-lg p-2 border border-blue-300">
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-blue-800 font-semibold text-sm">Total Monthly Cash Need:</span>
                        <span className="text-lg font-bold text-blue-900">{formatCurrency(wealthGap.monthly_cash_need)}</span>
                      </div>

                      {/* Allocation Breakdown */}
                      {(wealthGap.monthly_cash_need_from_non_business > 0 || wealthGap.monthly_cash_need_from_business > 0 || wealthGap.outside_income_monthly > 0) && (
                        <div className="mt-2 pt-2 border-t border-blue-200 space-y-1">
                          <p className="text-xs text-blue-700 font-semibold mb-1">How this is allocated:</p>

                          {wealthGap.monthly_cash_need_from_non_business > 0 && (
                            <div className="flex justify-between items-center text-xs bg-purple-50 rounded px-2 py-1">
                              <span className="text-purple-800">
                                Non-Business Net Worth ({wealthGap.non_business_source_category?.replace('_', ' ') || 'other assets'}):
                              </span>
                              <span className="font-bold text-purple-900">{formatCurrency(wealthGap.monthly_cash_need_from_non_business)}</span>
                            </div>
                          )}

                          {wealthGap.monthly_cash_need_from_business > 0 && (
                            <div className="flex justify-between items-center text-xs bg-blue-50 rounded px-2 py-1">
                              <span className="text-blue-800">From Business (Owner's Draw/Salary):</span>
                              <span className="font-bold text-blue-900">{formatCurrency(wealthGap.monthly_cash_need_from_business)}</span>
                            </div>
                          )}

                          {wealthGap.outside_income_monthly > 0 && (
                            <div className="flex justify-between items-center text-xs bg-green-50 rounded px-2 py-1">
                              <span className="text-green-800">Outside Income Sources:</span>
                              <span className="font-bold text-green-900">{formatCurrency(wealthGap.outside_income_monthly)}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <ul className="space-y-0.5 text-xs text-gray-700 ml-2 list-disc">
                      <li className="flex justify-between items-center">
                        <span>Lifestyle change at exit:</span>
                        <span className="font-semibold mr-2">{wealthGap.lifestyle_change_percentage || 0}%</span>
                      </li>
                      <li className="flex justify-between items-center">
                        <span>Annual inflation rate:</span>
                        <span className="font-semibold mr-2">{wealthGap.annual_inflation_rate || 2}%</span>
                      </li>
                      <li className="flex justify-between items-center">
                        <span>Monthly need at exit:</span>
                        <span className="font-semibold mr-2">{formatCurrency(monthlyNeedAtExit)}</span>
                      </li>
                    </ul>
                  </div> 
                </div>

                {/* Outside Income Sources */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-2">
                  <h5 className="font-semibold text-green-900 mb-1 text-sm">Outside Income Sources</h5>
                  <ul className="space-y-0.5 text-sm text-gray-700 ml-4 list-disc">
                    <li className="flex justify-between items-center">
                      <span>Current monthly cash need:</span>
                      <span className="font-semibold mr-2">{formatCurrency(wealthGap.monthly_cash_need)}</span>
                    </li>
                    <li className="flex justify-between items-center">
                      <span>Current monthly outside income:</span>
                      <span className="font-semibold mr-2">{formatCurrency(wealthGap.outside_income_monthly || 0)}</span>
                    </li>
                    <li className="flex justify-between items-center">
                      <span>Current net monthly need:</span>
                      <span className="font-semibold mr-2">{formatCurrency(currentNetMonthlyNeed)}</span>
                    </li>
                    <li className="flex justify-between items-center">
                      <span>Income grows at:</span>
                      <span className="font-semibold mr-2">{wealthGap.outside_income_annual_increase || 0}% annually</span>
                    </li>
                    <li className="flex justify-between items-center">
                      <span>Net monthly need at exit:</span>
                      <span className="font-semibold mr-2">{formatCurrency(netMonthlyNeed)}</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Row 3: Personal Net Worth and Total Capital Needed */}
              <div className="grid grid-cols-2 gap-3">
                {/* Personal Net Worth */}
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2">
                  <h5 className="font-semibold text-emerald-900 mb-1 text-sm">Personal Net Worth</h5>
                  <ul className="space-y-0.5 text-sm text-gray-700 ml-4 list-disc">
                    <li className="flex justify-between items-center">
                      <span>Current net worth:</span>
                      <span className="font-semibold mr-2">{formatCurrency(wealthGap.current_net_worth || 0)}</span>
                    </li>
                    <li className="flex justify-between items-center">
                      <span>Liquid assets:</span>
                      <span className="font-semibold mr-2">
                        {formatCurrency(wealthGap.liquid_assets || 0)} @ {wealthGap.liquid_assets_return_rate || 2}%
                        {nonBusinessSourceCategory === 'liquid_assets' && monthlyWithdrawal > 0 && (
                          <span className="text-orange-700"> (withdrawal: {formatCurrency(monthlyWithdrawal)}/mo)</span>
                        )}
                      </span>
                    </li>
                    <li className="flex justify-between items-center">
                      <span>Retirement accounts:</span>
                      <span className="font-semibold mr-2">
                        {formatCurrency(wealthGap.retirement_accounts || 0)} @ {wealthGap.retirement_accounts_return_rate || 7}%
                        {nonBusinessSourceCategory === 'retirement_accounts' && monthlyWithdrawal > 0 && (
                          <span className="text-orange-700"> (withdrawal: {formatCurrency(monthlyWithdrawal)}/mo)</span>
                        )}
                      </span>
                    </li>
                    <li className="flex justify-between items-center">
                      <span>Real estate:</span>
                      <span className="font-semibold mr-2">
                        {formatCurrency(wealthGap.real_estate_equity || 0)} @ {wealthGap.real_estate_return_rate || 2}%
                        {nonBusinessSourceCategory === 'real_estate' && monthlyWithdrawal > 0 && (
                          <span className="text-orange-700"> (withdrawal: {formatCurrency(monthlyWithdrawal)}/mo)</span>
                        )}
                      </span>
                    </li>
                    <li className="flex justify-between items-center">
                      <span>Other investments:</span>
                      <span className="font-semibold mr-2">
                        {formatCurrency(wealthGap.other_investments || 0)} @ {wealthGap.other_investments_return_rate || 7}%
                        {nonBusinessSourceCategory === 'other_investments' && monthlyWithdrawal > 0 && (
                          <span className="text-orange-700"> (withdrawal: {formatCurrency(monthlyWithdrawal)}/mo)</span>
                        )}
                      </span>
                    </li>
                    <li className="flex justify-between items-center">
                      <span>Liabilities:</span>
                      <span className="font-semibold mr-2 text-red-700">-{formatCurrency(wealthGap.total_liabilities || 0)}</span>
                    </li>
                  </ul>
                </div>

                {/* Total Capital Needed */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-2">
                  <h5 className="font-semibold text-blue-900 mb-1 text-sm">Total Capital Needed</h5>
                  <div className="bg-white/70 rounded p-2 border border-blue-300">
                    <p className="text-xs text-blue-800 font-semibold mb-2">Present Value Calculation:</p>
                    <ul className="space-y-1 text-xs text-gray-700 ml-2">
                      <li className="flex justify-between items-center">
                        <span className="text-blue-700">Net Monthly Need at Exit:</span>
                        <span className="font-semibold text-blue-900 text-sm">{formatCurrency(netMonthlyNeed)}</span>
                      </li>
                      <li className="flex justify-between items-center">
                        <span className="text-blue-700">Years to Fund:</span>
                        <span className="font-semibold mr-2">{(wealthGap.life_expectancy || 95) - ageAtExit} years (age {ageAtExit} to {wealthGap.life_expectancy || 95})</span>
                      </li>
                      <li className="flex justify-between items-center">
                        <span className="text-blue-700">Investment Return:</span>
                        <span className="font-semibold mr-2">{wealthGap.annual_return_rate || 7}% annually</span>
                      </li>
                      <li className="flex justify-between items-center">
                        <span className="text-blue-700">Inflation Rate:</span>
                        <span className="font-semibold mr-2">{wealthGap.annual_inflation_rate || 2}% annually</span>
                      </li>
                    </ul>
                    <div className="mt-2 pt-2 border-t border-blue-300">
                      <div className="flex justify-between items-center space-y-1">
                        <span className="text-blue-900 font-bold text-sm">Total Capital Needed:</span>
                        <span className="font-bold text-base text-blue-700 mr-2">{formatCurrency(requiredData.total_capital_needed || 0)}</span>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-blue-600 mt-2 italic">This is the lump sum needed at exit to fund all future monthly needs, accounting for investment growth and inflation.</p>
                </div>
              </div>

              {/* Disclaimer & Disclosure (Full Width) */}
              <div className="bg-rose-50 border border-rose-300 rounded-lg p-2 mt-3">
                <h5 className="font-semibold text-rose-900 mb-1 text-sm">Disclosure & Disclaimer</h5>
                <div className="text-xs text-rose-700">
                  <p>This analysis calculates total capital needed and accounts for your personal net worth growth to determine what your business must provide. The calculation may not fit all situations or local market conditions. This is a best-effort, automated projection based on the assumptions above. <span className="font-semibold">Consult with a financial advisor and/or accountant for personalized guidance.</span></p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PAGE 4: Detailed Calculation Flow - Only for Monthly Needs Method */}
      {isMonthlyNeeds && hasRequiredData && (
        <div className="print-page">
          <div className="pdf-header">
            <img src="/dominion-header.png" alt="Dominion Business Advisors" />
          </div>

          <div style={{ paddingTop: '0.1in', paddingLeft: '0.3in', paddingRight: '0.3in' }}>
            <h4 className="font-bold text-base text-gray-900 mb-2 flex items-center gap-4">
              Detailed Calculation Flow
            </h4>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              {/* LEFT COLUMN: Steps 1-3 */}
              <div className="space-y-3">
              {/* Step 1: Current Monthly Lifestyle Needs */}
              <div className="bg-gradient-to-r from-blue-50 to-blue-100 border-l-4 border-blue-500 rounded-lg p-1.5">
                <h5 className="font-bold text-blue-900 mb-1 flex items-center gap-2" style={{ fontSize: '12px' }}>
                  <span className="bg-blue-500 text-white rounded-full w-4 h-4 flex items-center justify-center" style={{ fontSize: '11px' }}>1</span>
                  Current Monthly Lifestyle Needs
                </h5>
                <div className="space-y-0.5" style={{ fontSize: '11px' }}>
                  <div className="flex justify-between items-center">
                    <span className="text-blue-800">Current Monthly Cash Need:</span>
                    <span className="font-bold text-blue-900">{formatCurrency(wealthGap.monthly_cash_need)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-blue-800">Current Monthly Outside Income:</span>
                    <span className="font-bold text-green-700">- {formatCurrency(wealthGap.outside_income_monthly || 0)}</span>
                  </div>
                  <div className="border-t-2 border-blue-300 pt-0.5 flex justify-between items-center">
                    <span className="text-blue-900 font-semibold">Current Net Monthly Need:</span>
                    <span className="font-bold text-blue-700" style={{ fontSize: '11px' }}>{formatCurrency(currentNetMonthlyNeed)}</span>
                  </div>
                </div>
              </div>

              {/* Step 2: Projection to Exit Year */}
              <div className="bg-gradient-to-r from-purple-50 to-purple-100 border-l-4 border-purple-500 rounded-lg p-1.5">
                <h5 className="font-bold text-purple-900 mb-1 flex items-center gap-2" style={{ fontSize: '12px' }}>
                  <span className="bg-purple-500 text-white rounded-full w-4 h-4 flex items-center justify-center" style={{ fontSize: '11px' }}>2</span>
                  Projection to Exit Year ({wealthGap.exit_year})
                </h5>
                <div className="space-y-1" style={{ fontSize: '11px' }}>
                  {/* Monthly Cash Need Projection */}
                  <div className="bg-white/50 rounded p-1">
                    <p className="text-purple-800 font-semibold mb-0.5">Monthly Cash Need Growth:</p>
                    <div className="space-y-0.5 ml-2">
                      <div className="flex justify-between">
                        <span className="text-purple-700">Starting:</span>
                        <span className="font-semibold">{formatCurrency(wealthGap.monthly_cash_need)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-purple-700">× Inflation ({wealthGap.annual_inflation_rate}%, {yearsUntilExit}yr):</span>
                        <span className="font-semibold">{inflationMultiplier.toFixed(3)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-purple-700">× Lifestyle ({wealthGap.lifestyle_change_percentage > 0 ? '+' : ''}{wealthGap.lifestyle_change_percentage}%):</span>
                        <span className="font-semibold">{(1 + wealthGap.lifestyle_change_percentage / 100).toFixed(3)}</span>
                      </div>
                      <div className="border-t border-purple-300 pt-0.5 flex justify-between">
                        <span className="text-purple-900 font-semibold">= Need at Exit:</span>
                        <span className="font-bold text-purple-900">{formatCurrency(monthlyNeedAtExit)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Outside Income Projection */}
                  <div className="bg-white/50 rounded p-1">
                    <p className="text-purple-800 font-semibold mb-0.5">Outside Income Growth:</p>
                    <div className="space-y-0.5 ml-2">
                      <div className="flex justify-between">
                        <span className="text-purple-700">Starting:</span>
                        <span className="font-semibold">{formatCurrency(wealthGap.outside_income_monthly || 0)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-purple-700">× Growth ({wealthGap.outside_income_annual_increase}%, {yearsUntilRetirement}yr):</span>
                        <span className="font-semibold">{Math.pow(1 + outsideIncomeGrowthRate, yearsUntilRetirement).toFixed(3)}</span>
                      </div>
                      <div className="border-t border-purple-300 pt-0.5 flex justify-between">
                        <span className="text-purple-900 font-semibold">= Income at Exit:</span>
                        <span className="font-bold text-green-700">{formatCurrency(futureOutsideIncomeMonthly)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Net Monthly Need at Exit */}
                  <div className="bg-gradient-to-r from-emerald-100 to-teal-100 rounded p-1 border-2 border-emerald-500">
                    <div className="space-y-0.5">
                      <div className="flex justify-between items-center">
                        <span className="text-emerald-800">Monthly Need at Exit:</span>
                        <span className="font-bold">{formatCurrency(monthlyNeedAtExit)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-emerald-800">Outside Income at Exit:</span>
                        <span className="font-bold text-green-700">- {formatCurrency(futureOutsideIncomeMonthly)}</span>
                      </div>
                      <div className="border-t-2 border-emerald-600 pt-0.5 flex justify-between items-center">
                        <span className="text-emerald-900 font-bold">Net Monthly Need at Exit:</span>
                        <span className="font-bold text-emerald-700" style={{ fontSize: '12px' }}>{formatCurrency(netMonthlyNeed)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 3: Total Capital Needed */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-100 border-l-4 border-blue-500 rounded-lg p-1.5">
                <h5 className="font-bold text-blue-900 mb-1 flex items-center gap-2" style={{ fontSize: '12px' }}>
                  <span className="bg-blue-500 text-white rounded-full w-4 h-4 flex items-center justify-center" style={{ fontSize: '11px' }}>3</span>
                  Total Capital Needed
                </h5>
                <div className="space-y-1" style={{ fontSize: '11px' }}>
                  <div className="bg-white/50 rounded p-1">
                    <p className="text-blue-800 font-semibold mb-0.5">Present Value Calculation:</p>
                    <div className="space-y-0.5 ml-2">
                      <div className="flex justify-between">
                        <span className="text-blue-700">Net Monthly Need:</span>
                        <span className="font-semibold">{formatCurrency(netMonthlyNeed)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-blue-700">Years to Fund:</span>
                        <span className="font-semibold">{wealthGap.life_expectancy - ageAtExit}yr ({ageAtExit}-{wealthGap.life_expectancy})</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-blue-700">Investment Return:</span>
                        <span className="font-semibold">{wealthGap.annual_return_rate}%/yr</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-blue-700">Inflation Rate:</span>
                        <span className="font-semibold">{wealthGap.annual_inflation_rate}%/yr</span>
                      </div>
                      <div className="border-t border-blue-300 pt-0.5 flex justify-between">
                        <span className="text-blue-900 font-semibold">Total Capital Needed:</span>
                        <span className="font-bold text-blue-900">{formatCurrency(requiredData.total_capital_needed)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              </div>

              {/* RIGHT COLUMN: Steps 4-6 */}
              <div className="space-y-3">
              {/* Step 4: Personal Net Worth Factor */}
              <div className="bg-gradient-to-r from-emerald-50 to-teal-100 border-l-4 border-emerald-500 rounded-lg p-1.5">
                <h5 className="font-bold text-emerald-900 mb-1 flex items-center gap-2" style={{ fontSize: '12px' }}>
                  <span className="bg-emerald-500 text-white rounded-full w-10 h-4 flex items-center justify-center" style={{ fontSize: '11px' }}>4 & 5</span>
                  Personal Net Worth Factor
                </h5>
                <div className="space-y-1" style={{ fontSize: '11px' }}>
                  <div className="bg-white/50 rounded p-1">
                    <p className="text-emerald-800 font-semibold mb-0.5">Future Value of Personal Assets:</p>
                    <div className="space-y-0.5 ml-2">
                      <div className="flex justify-between">
                        <span className="text-emerald-700">Current Net Worth:</span>
                        <span className="font-semibold">{formatCurrency(wealthGap.current_net_worth)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-emerald-700">Return Rate:</span>
                        <span className="font-semibold">See Assumptions Above</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-emerald-700">Years to Exit:</span>
                        <span className="font-semibold">{yearsUntilExit}yr</span>
                      </div>
                      <div className="border-t border-emerald-300 pt-0.5 flex justify-between">
                        <span className="text-emerald-900 font-semibold">Future Net Worth:</span>
                        <span className="font-bold text-emerald-900">{formatCurrency(futureNetWorth)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Net Gap Calculation */}
                  <div className={`rounded p-1 border-2 ${localWealthSurplus > 0 ? 'bg-gradient-to-r from-green-100 to-emerald-200 border-green-500' : 'bg-gradient-to-r from-orange-100 to-red-200 border-orange-500'}`}>
                    <div className="space-y-0.5">
                      <div className="flex justify-between items-center">
                        <span className={localWealthSurplus > 0 ? 'text-green-800' : 'text-orange-800'}>Total Capital:</span>
                        <span className="font-bold">{formatCurrency(requiredData.total_capital_needed)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className={localWealthSurplus > 0 ? 'text-green-800' : 'text-orange-800'}>Future Net Worth:</span>
                        <span className="font-bold text-green-700">- {formatCurrency(futureNetWorth)}</span>
                      </div>
                      <div className="border-t-2 border-current pt-0.5 flex justify-between items-center">
                        {localWealthSurplus > 0 ? (
                          <>
                            <span className="font-bold text-green-900">Wealth Surplus:</span>
                            <span className="font-bold text-green-700" style={{ fontSize: '12px' }}>{formatCurrency(localWealthSurplus)}</span>
                          </>
                        ) : (
                          <>
                            <span className="font-bold text-orange-900">Gap from Business:</span>
                            <span className="font-bold text-orange-700" style={{ fontSize: '12px' }}>{formatCurrency(localNetGapFromBusiness)}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 6: Required Sale Price (if gap exists) */}
              {localNetGapFromBusiness > 0 && (
                <div className="bg-gradient-to-r from-orange-50 to-red-100 border-l-4 border-orange-500 rounded-lg p-1.5">
                  <h5 className="font-bold text-orange-900 mb-1 flex items-center gap-2" style={{ fontSize: '12px' }}>
                    <span className="bg-orange-500 text-white rounded-full w-4 h-4 flex items-center justify-center" style={{ fontSize: '11px' }}>6</span>
                    Required Business Sale Price
                  </h5>
                  <div className="space-y-1" style={{ fontSize: '11px' }}>
                    <div className="bg-gradient-to-r from-red-100 to-orange-200 rounded p-1 border-2 border-red-500">
                      <p className="text-red-900 font-semibold mb-0.5">Calculating Required Sale Price:</p>
                      <div className="space-y-0.5 ml-2">
                        <div className="flex justify-between">
                          <span className="text-red-800">Gap (After-Tax):</span>
                          <span className="font-semibold">{formatCurrency(localNetGapFromBusiness)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-red-800">÷ Ownership:</span>
                          <span className="font-semibold">{wealthGap.ownership_percentage}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-red-800">÷ After-Tax (1-{wealthGap.tax_rate_on_sale}%):</span>
                          <span className="font-semibold">{((1 - taxDecimal) * 100).toFixed(1)}%</span>
                        </div>
                        <div className="border-t-2 border-red-600 pt-0.5 flex justify-between items-center">
                          <span className="text-red-900 font-bold">Required Price:</span>
                          <span className="font-bold text-red-700" style={{ fontSize: '12px' }}>{formatCurrency(localRequiredSalePrice)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 7: Business Value Analysis */}
              {futureBusinessValueDisplay > 0 && (
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 border-l-4 border-gray-500 rounded-lg p-1.5">
                  <h5 className="font-bold text-gray-900 mb-1 flex items-center gap-2" style={{ fontSize: '12px' }}>
                    <span className="bg-gray-500 text-white rounded-full w-4 h-4 flex items-center justify-center" style={{ fontSize: '11px' }}>7</span>
                    Business Value Analysis
                  </h5>
                  <div className="space-y-1" style={{ fontSize: '11px' }}>
                    {/* Future Value Calculation */}
                    <div className="bg-white/50 rounded p-1">
                      <p className="text-gray-800 font-semibold mb-0.5">Future Business Value:</p>
                      <div className="space-y-0.5 ml-2">
                        <div className="flex justify-between">
                          <span className="text-gray-700">Current Value (PV):</span>
                          <span className="font-semibold text-blue-700">{formatCurrency(wealthGap.desired_sale_price || 0)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-700">Growth Rate:</span>
                          <span className="font-semibold">{wealthGap.business_growth_rate}%/yr</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-700">Years:</span>
                          <span className="font-semibold">{yearsUntilExit}yr</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-700">Growth Factor:</span>
                          <span className="font-semibold">{Math.pow(1 + (wealthGap.business_growth_rate || 0) / 100, yearsUntilExit).toFixed(3)}</span>
                        </div>
                        <div className="border-t border-gray-300 pt-0.5 flex justify-between">
                          <span className="text-gray-900 font-semibold">Future Value (FV):</span>
                          <span className="font-bold text-blue-600">{formatCurrency(futureBusinessValueDisplay)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Comparison */}
                    {localNetGapFromBusiness > 0 ? (
                      <>
                        <div className="bg-white/50 rounded p-1">
                          <div className="flex justify-between items-center">
                            <span className="text-gray-700">Required:</span>
                            <span className="font-bold text-orange-600">{formatCurrency(localRequiredSalePrice)}</span>
                          </div>
                        </div>
                        <div className={`border-t-2 pt-0.5 flex justify-between items-center ${futureBusinessValueDisplay >= localRequiredSalePrice ? 'border-green-500' : 'border-red-500'}`}>
                          <span className="font-bold text-gray-900">Gap/Surplus:</span>
                          <span className={`font-bold ${futureBusinessValueDisplay >= localRequiredSalePrice ? 'text-green-600' : 'text-red-600'}`} style={{ fontSize: '11px' }}>
                            {futureBusinessValueDisplay >= localRequiredSalePrice ? '+' : ''}{formatCurrency(futureBusinessValueDisplay - localRequiredSalePrice)}
                          </span>
                        </div>
                        {futureBusinessValueDisplay >= localRequiredSalePrice ? (
                          <p className="text-green-700 font-semibold bg-green-50 p-1 rounded" style={{ fontSize: '11px' }}>
                            ✓ Future value exceeds required amount!
                          </p>
                        ) : (
                          <p className="text-red-700 font-semibold bg-red-50 p-1 rounded" style={{ fontSize: '11px' }}>
                            ⚠ Below required amount. Focus on value increase.
                          </p>
                        )}
                      </>
                    ) : (
                      <div className="bg-green-50 border border-green-200 rounded p-1">
                        <p className="text-green-800 font-semibold" style={{ fontSize: '11px' }}>
                          ✓ Personal net worth covers needs. {formatCurrency(futureBusinessValueDisplay)} is additional wealth!
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PAGE 5: Financial Summary at Exit & What This Means - Only for Monthly Needs Method */}
      {isMonthlyNeeds && hasRequiredData && (
        <div className="print-page">
          <div className="pdf-header">
            <img src="/dominion-header.png" alt="Dominion Business Advisors" />
          </div>

          <div style={{ padding: '0.08in 0' }}>
            <div className="space-y-0.5">
              <h4 className="font-bold text-base text-gray-900 mb-0.5 flex items-center gap-2">
                What This Means For You
              </h4>
              {/* What This Means For You */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-1">
                  <div className="text-xs text-gray-700 space-y-1">
                    {/* Lifestyle Needs Overview */}
                    <div className="bg-white rounded-lg p-1 border border-blue-200">
                      <h5 className="font-semibold text-blue-900 mb-0.5 text-xs">Your Lifestyle Funding Needs</h5>
                      <p className="mb-0.5 text-xs">
                        To maintain your desired lifestyle from exit through age {wealthGap.life_expectancy} ({wealthGap.life_expectancy - ageAtExit} years), you may need a total of <strong className="text-blue-700">{formatCurrency(requiredData.total_capital_needed)}</strong> in capital at your exit date.
                      </p>
                      <div className="text-xs text-gray-600 ml-3 space-y-0.5">
                        <p>• Current monthly need: <strong>{formatCurrency(wealthGap.monthly_cash_need)}</strong></p>
                        <p>• Less outside income: <strong>-{formatCurrency(wealthGap.outside_income_monthly || 0)}</strong></p>
                        <p>• Current net monthly need: <strong className="text-blue-600">{formatCurrency(currentNetMonthlyNeed)}</strong></p>
                        <p>• Projected to exit year {wealthGap.exit_year}: <strong className="text-blue-700">{formatCurrency(netMonthlyNeed)}/month</strong></p>
                        <p>• This accounts for {wealthGap.annual_inflation_rate}% inflation and {wealthGap.outside_income_annual_increase}% annual growth in outside income</p>
                      </div>
                    </div>

                    {/* Personal Net Worth Factor */}
                    <div className={`rounded-lg p-1 border-2 ${localWealthSurplus > 0 ? 'bg-green-50 border-green-300' : 'bg-orange-50 border-orange-300'}`}>
                      <h5 className={`font-semibold mb-0.5 text-xs ${localWealthSurplus > 0 ? 'text-green-900' : 'text-orange-900'}`}>
                        Your Personal Net Worth Impact
                      </h5>
                      <p className="mb-0.5 text-xs">
                        Your current personal net worth of <strong>{formatCurrency(wealthGap.current_net_worth)}</strong> (excluding business value) may grow to approximately <strong className={localWealthSurplus > 0 ? 'text-green-700' : 'text-orange-700'}>{formatCurrency(futureNetWorth)}</strong> by {wealthGap.exit_year}, applying the annual return rate from the assumptions above.
                      </p>
                      {localWealthSurplus > 0 ? (
                        <>
                          <p className="font-semibold text-green-800 mb-0.5 text-xs">
                            Excellent Financial Position!
                          </p>
                          <p className="mb-0.5 text-xs">
                            Your personal assets may grow to <strong className="text-green-700">{formatCurrency(localWealthSurplus)}</strong> MORE than needed to sustain your lifestyle. This could mean:
                          </p>
                          <ul className="text-xs ml-3 space-y-0.5 text-green-800">
                            <li>✓ You may be able to exit your business with greater flexibility</li>
                            <li>✓ Any business sale proceeds may be <strong>additional wealth</strong> beyond your needs</li>
                            <li>✓ You may have flexibility to pursue passion projects or philanthropy</li>
                            <li>✓ Potential for legacy planning for heirs or charitable giving</li>
                          </ul>
                        </>
                      ) : (
                        <>
                          <p className="font-semibold text-orange-800 mb-0.5 text-xs">
                            Business Value May Be Required
                          </p>
                          <p className="mb-0.5 text-xs">
                            After your personal net worth growth, your business may need to provide <strong className="text-orange-700">{formatCurrency(localNetGapFromBusiness)}</strong> in after-tax proceeds to cover the remaining gap.
                          </p>
                          <div className="text-xs ml-3 space-y-0.5">
                            <p>• Total capital needed: <strong>{formatCurrency(requiredData.total_capital_needed)}</strong></p>
                            <p>• Personal net worth provides: <strong className="text-green-700">-{formatCurrency(futureNetWorth)}</strong></p>
                            <p>• Gap from business sale: <strong className="text-orange-700">{formatCurrency(localNetGapFromBusiness)}</strong></p>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Business Sale Requirements / No Sale Required */}
                    {localNetGapFromBusiness > 0 ? (
                      <div className="bg-white rounded-lg p-1 border border-orange-200">
                        <h5 className="font-semibold text-orange-900 mb-0.5 text-xs">Potential Business Sale Price Needed</h5>
                        <p className="mb-1 text-xs">
                          To net <strong>{formatCurrency(localNetGapFromBusiness)}</strong> after taxes from your business sale, with <strong>{wealthGap.ownership_percentage}%</strong> ownership and a <strong>{wealthGap.tax_rate_on_sale}%</strong> tax rate on the sale, your business may need to sell for:
                        </p>
                        <div className="bg-orange-50 rounded p-0.5 mb-0.5 border border-orange-200">
                         <p className="text-xs text-center text-orange-600">Pre-tax business valuation needed: <span className="text-sm font-bold text-orange-700">{formatCurrency(localRequiredSalePrice)}</span>
                         </p>
                        </div>
                        <div className="text-xs text-gray-600 space-y-0.5">
                          <p><strong>Tax Calculation:</strong></p>
                          <p className="ml-3">• Sale price: <strong>{formatCurrency(localRequiredSalePrice)}</strong></p>
                          <p className="ml-3">• Your ownership ({wealthGap.ownership_percentage}%): <strong>{formatCurrency(localRequiredSalePrice * (wealthGap.ownership_percentage / 100))}</strong></p>
                          <p className="ml-3">• Less taxes ({wealthGap.tax_rate_on_sale}%): <strong className="text-red-600">-{formatCurrency(localRequiredSalePrice * (wealthGap.ownership_percentage / 100) * (wealthGap.tax_rate_on_sale / 100))}</strong></p>
                          <p className="ml-3 border-t border-gray-300 pt-0.5">• After-tax proceeds: <strong className="text-green-700">{formatCurrency(afterTaxProceeds)}</strong></p>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-green-50 rounded-lg p-1 border border-green-300">
                        <h5 className="font-semibold text-green-900 mb-0.5 text-xs">Business Sale May Not Be Required</h5>
                        <p className="text-green-800 text-xs">
                          Since your personal net worth may cover lifestyle needs, any proceeds from selling your business may be <strong>additional wealth</strong>. Consider:
                        </p>
                        <ul className="text-xs mt-1 ml-3 space-y-0.5 text-green-800">
                          <li>• Enhancing your lifestyle or pursuing luxury goals</li>
                          <li>• Creating a substantial legacy for heirs</li>
                          <li>• Funding charitable causes or foundations</li>
                          <li>• Making strategic investments or acquisitions</li>
                          <li>• Taking more time to find the perfect buyer at the best price</li>
                        </ul>
                      </div>
                    )}

                    {/* Future Business Value Analysis */}
                    {futureBusinessValueDisplay > 0 && (
                      <div className="bg-white rounded-lg p-2 border border-gray-300">
                        <h5 className="font-semibold text-gray-900 mb-0.5 text-xs">Future Business Value at Exit</h5>
                        <p className="mb-0.5 text-xs">
                          Your business's projected value at exit is <strong className="text-blue-700">{formatCurrency(futureBusinessValueDisplay)}</strong>, which may provide after-tax proceeds to strengthen your financial position.
                        </p>
                        {localWealthSurplus > 0 ? (
                          <p className="text-green-700 text-xs">
                            ✓ This may add significantly to your already excellent financial position! You may be in a good position to exit when you choose.
                          </p>
                        ) : localNetGapFromBusiness > 0 ? (
                          <>
                            {futureBusinessValueDisplay >= localRequiredSalePrice ? (
                              <>
                                <p className="text-green-700 font-semibold mb-0.5 text-xs">
                                  ✓ Your future business value EXCEEDS the required amount!
                                </p>
                                <p className="text-xs text-gray-700">
                                  Your business's projected value is <strong>{formatCurrency(futureBusinessValueDisplay - localRequiredSalePrice)}</strong> above what you may need for your lifestyle goals. This may provide a financial cushion and exit flexibility.
                                </p>
                              </>
                            ) : (
                              <>
                                <p className="text-orange-700 font-semibold mb-1 text-xs">
                                  Value Gap: {formatCurrency(localRequiredSalePrice - futureBusinessValueDisplay)} may be needed
                                </p>
                                <p className="text-xs text-gray-700 mb-0.5">
                                  Your business may need to increase in value by <strong className="text-orange-700">{((localRequiredSalePrice / futureBusinessValueDisplay - 1) * 100).toFixed(1)}%</strong> to meet your financial goals.
                                </p>
                              </>
                            )}
                          </>
                        ) : null}
                      </div>
                    )}

                    {/* Timeline and Next Steps */}
                    <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg p-1 border border-purple-200">
                      <h5 className="font-semibold text-purple-900 mb-0.5 text-xs">Timeline & Action Items</h5>
                      <div className="text-xs space-y-1">
                        <p>
                          <strong>Time to Exit:</strong> {yearsUntilExit} years ({currentYear} → {wealthGap.exit_year})
                          <span className="text-xs text-gray-600"> You'll be age {ageAtExit} at exit, with {wealthGap.life_expectancy - ageAtExit} years to fund through age {wealthGap.life_expectancy}.</span>
                        </p>
                        <div className="mt-2 pt-2 border-t border-purple-200">
                          <p className="font-semibold text-purple-900 mb-1 text-xs">Recommended Next Steps:</p>
                          <ul className="text-xs space-y-0.5 ml-3 text-gray-700">
                            {localWealthSurplus > 0 ? (
                              <>
                                <li>✓ Review your exit planning checklist to optimize timing</li>
                                <li>✓ Consider tax strategies to maximize after-tax proceeds</li>
                                <li>✓ Explore estate planning to preserve wealth for heirs</li>
                                <li>✓ Evaluate whether to hold out for premium offers</li>
                              </>
                            ) : futureBusinessValueDisplay >= localRequiredSalePrice ? (
                              <>
                                <li>✓ Continue building value to create a larger cushion</li>
                                <li>✓ Focus on the highest-impact value drivers</li>
                                <li>✓ Begin exit planning and process preparation</li>
                                <li>✓ Consider timing the market for optimal sale conditions</li>
                              </>
                            ) : (
                              <>
                                <li>⚠ Prioritize tasks that directly increase business value</li>
                                <li>⚠ Address critical gaps in your readiness assessment</li>
                                <li>⚠ Consider extending timeline or adjusting lifestyle needs</li>
                                <li>⚠ Focus on profit improvement and revenue growth</li>
                                <li>⚠ Reduce owner dependency to increase marketability</li>
                                <li>⚠ Build management systems to increase marketability</li>
                              </>
                            )}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
      )}
    </>
  );
};

WealthGapPrintContent.propTypes = {
  wealthGap: PropTypes.object.isRequired
};

export default WealthGapPrintContent;
